import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';

interface PlatformEvent {
  id: number;
  occurredAt: string;
  source: string;
  eventType: string;
  userId?: number | null;
  visitorId?: string | null;
  telegramUserId?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  payload: unknown;
  userEmail?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
}

const LogsPage: React.FC = () => {
  const [events, setEvents] = useState<PlatformEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [source, setSource] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventTypePrefix, setEventTypePrefix] = useState(false);
  const [telegramUserId, setTelegramUserId] = useState('');
  const [ip, setIp] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tick, setTick] = useState(0);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        limit,
        eventTypePrefix,
      };
      if (userId.trim()) params.userId = parseInt(userId.trim(), 10);
      if (userEmail.trim()) params.userEmail = userEmail.trim();
      if (source.trim()) params.source = source.trim();
      if (eventType.trim()) params.eventType = eventType.trim();
      if (telegramUserId.trim()) params.telegramUserId = telegramUserId.trim();
      if (ip.trim()) params.ip = ip.trim();
      if (dateFrom) params.dateFrom = new Date(dateFrom).toISOString();
      if (dateTo) params.dateTo = new Date(dateTo).toISOString();

      const res = await api.get('/admin/platform-events', { params });
      if (res.data.success) {
        setEvents(res.data.events || []);
        const p = res.data.pagination;
        setTotal(p?.total ?? 0);
        setTotalPages(p?.pages ?? 1);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Ошибка загрузки логов');
    } finally {
      setLoading(false);
    }
  }, [page, tick, userId, userEmail, source, eventType, eventTypePrefix, telegramUserId, ip, dateFrom, dateTo]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fmt = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('ru-RU');
    } catch {
      return iso;
    }
  };

  const payloadStr = (p: unknown) => {
    if (p == null) return '—';
    try {
      return JSON.stringify(p);
    } catch {
      return String(p);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Логи событий</h1>
          <p className="text-gray-600 mt-2">
            Единая лента: авторизация, Telegram, оплаты, резюме, консультант и др.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchEvents()}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Обновить
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="block text-sm">
            <span className="text-gray-600">User ID</span>
            <input
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="напр. 29"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Email (частично)</span>
            <input
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="yandex"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Источник (source)</span>
            <input
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="telegram, auth, payment, resume, core"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Тип события (eventType)</span>
            <input
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              placeholder="user.login, telegram.message"
            />
          </label>
          <label className="flex items-center gap-2 text-sm mt-6">
            <input
              type="checkbox"
              checked={eventTypePrefix}
              onChange={(e) => setEventTypePrefix(e.target.checked)}
            />
            Префикс для eventType (LIKE)
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Telegram user id</span>
            <input
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={telegramUserId}
              onChange={(e) => setTelegramUserId(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">IP (частично)</span>
            <input
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Дата от</span>
            <input
              type="datetime-local"
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Дата до</span>
            <input
              type="datetime-local"
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setPage(1);
              setTick((t) => t + 1);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
          >
            Применить фильтры
          </button>
          <button
            type="button"
            onClick={() => {
              setUserId('');
              setUserEmail('');
              setSource('');
              setEventType('');
              setEventTypePrefix(false);
              setTelegramUserId('');
              setIp('');
              setDateFrom('');
              setDateTo('');
              setPage(1);
              setTick((t) => t + 1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm"
          >
            Сбросить
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 text-sm text-gray-600">
          Всего записей: {total}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Время</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Источник</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Событие</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Пользователь</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telegram</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payload</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    Загрузка…
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    Нет событий
                  </td>
                </tr>
              ) : (
                events.map((ev) => (
                  <tr key={ev.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm whitespace-nowrap">{fmt(ev.occurredAt)}</td>
                    <td className="px-3 py-2 text-sm">{ev.source}</td>
                    <td className="px-3 py-2 text-sm font-mono text-xs">{ev.eventType}</td>
                    <td className="px-3 py-2 text-sm">
                      {ev.userId != null ? (
                        <span>
                          #{ev.userId}
                          {ev.userEmail ? (
                            <span className="block text-xs text-gray-500">{ev.userEmail}</span>
                          ) : null}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm font-mono text-xs">{ev.telegramUserId || '—'}</td>
                    <td className="px-3 py-2 text-sm font-mono text-xs">{ev.ipAddress || '—'}</td>
                    <td className="px-3 py-2 text-xs font-mono max-w-md truncate" title={payloadStr(ev.payload)}>
                      {payloadStr(ev.payload)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Назад
            </button>
            <span className="text-sm text-gray-600">
              Страница {page} из {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Вперёд
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsPage;
